import { computed, ref, type Ref } from 'vue';
import { useCustomToast } from '@/shared/lib';
import { type BranchMember } from '@/entities/Branch';
import { trainingMatrixApi, type TrainingMatrixRevisionListItem } from '@/entities/Training';
import { getMemberPositions } from '../lib/getMemberPositions';
import { fetchAllActiveMembers } from '../lib/fetchAllActiveMembers';

const REVISIONS_ITEMS_PER_PAGE = 100;

export function buildMemberOptions(members: BranchMember[]) {
  return members
    .filter((member): member is BranchMember & { id: number } => member.id !== undefined)
    .map((member) => ({ id: member.id, name: member.user?.name ?? '' }));
}

/**
 * Shared by every training creation form: the paginated members/matrix-revisions
 * fetch, the single-position auto-fill rule, and the loading/loaded lifecycle.
 */
export function useTrainingFormResources() {
  const toast = useCustomToast();

  const members = ref<BranchMember[]>([]);
  const revisions = ref<TrainingMatrixRevisionListItem[]>([]);
  const loading = ref(false);
  const loaded = ref(false);

  const memberOptions = computed(() => buildMemberOptions(members.value));

  let inFlightLoad: Promise<void> | null = null;

  async function fetchAllMatrixRevisions(): Promise<TrainingMatrixRevisionListItem[]> {
    const firstRevisionsPage = await trainingMatrixApi.listRevisions({
      page: 1,
      itemsPerPage: REVISIONS_ITEMS_PER_PAGE,
    });
    const collected = [...firstRevisionsPage.data];
    let page = 2;
    let hasNextPage = firstRevisionsPage.metadata.isNextPage;
    while (hasNextPage) {
      const result = await trainingMatrixApi.listRevisions({
        page,
        itemsPerPage: REVISIONS_ITEMS_PER_PAGE,
      });
      collected.push(...result.data);
      hasNextPage = result.metadata.isNextPage;
      page += 1;
    }
    return collected;
  }

  async function load(defaultRevisionId: Ref<number | null>): Promise<void> {
    if (loaded.value) return;
    if (inFlightLoad) return inFlightLoad;

    inFlightLoad = (async () => {
      loading.value = true;
      try {
        const [allMembers, allRevisions] = await Promise.all([
          fetchAllActiveMembers(),
          fetchAllMatrixRevisions(),
        ]);
        members.value = allMembers;
        revisions.value = allRevisions;
        if (defaultRevisionId.value === null) {
          defaultRevisionId.value = allRevisions[0]?.id ?? null;
        }
        loaded.value = true;
      } catch {
        toast.showErrorToast({
          title: 'Erro ao carregar dados do formulário',
          message: 'Não foi possível carregar colaboradores e revisões da matriz. Tente novamente.',
          type: 'error',
        });
      } finally {
        loading.value = false;
        inFlightLoad = null;
      }
    })();

    return inFlightLoad;
  }

  function resolvePositionForMember(memberId: number | null): number | null {
    const positions = getMemberPositions(members.value, memberId);
    return positions.length === 1 ? positions[0]!.id : null;
  }

  return {
    members,
    revisions,
    loading,
    loaded,
    memberOptions,
    load,
    resolvePositionForMember,
  };
}
